import * as mutations from "../../graphql/mutations";
import * as queries from "../../graphql/queries";
import { API, graphqlOperation } from "aws-amplify";
import { GraphQLResult, GRAPHQL_AUTH_MODE } from "@aws-amplify/api";

// Configure Amplify
import Amplify from "aws-amplify";
import awsmobile from "../../aws-exports";

import formatResult from "../ReturnType/ReturnType";
import { CreateTaxRateInput, CreateTaxRateMutation, DeleteTaxRateInput, DeleteTaxRateMutation, GetTaxRateQuery, ListTaxRatesQuery, UpdateTaxRateInput, UpdateTaxRateMutation } from "../../API";
import moment from "moment";
import { TaxRate } from "../../models";

Amplify.configure(awsmobile);

/**
* Handle creating a new record in the database for type: taxRate. 
* 
* @param {object}  input                   Check schema for input.
* @param {string}  authMode                API_KEY (default), AWS_AIM or AMAZON_COGNITO_USER_POOLS
* 
* @return {string} Returns the newly created taxRate.
*/
export async function createTaxRate(input: CreateTaxRateInput, authMode?: GRAPHQL_AUTH_MODE) {
   if (!input) return formatResult(false, "TaxRate", "No input", "Create TaxRate received no input.");
   try {
        const fullInput: CreateTaxRateInput = {
            ...input,
            createdOn: moment(new Date()).format("YYYY-MM-DDTHH:mm:ss.SSSZ"),
            updatedOn: moment(new Date()).format("YYYY-MM-DDTHH:mm:ss.SSSZ")
        };
        let result;
        if (!authMode) result = (await API.graphql(graphqlOperation(mutations.createTaxRate, { input: fullInput }))) as GraphQLResult<CreateTaxRateMutation>;
        else result = (await API.graphql({
            query: mutations.createTaxRate,
            variables: {
                input: fullInput
            },
            authMode: authMode || GRAPHQL_AUTH_MODE.API_KEY
        })) as GraphQLResult<CreateTaxRateMutation>;
        const taxRate = result.data?.createTaxRate;
        return formatResult(true, "TaxRate", taxRate, "Successfully created the taxRate.");
    } catch (error: any) {
        return formatResult(false, "TaxRate", error, "Error creating record in the database for type: taxRate");
    }
}

/**
* Handle updating a new record in the database for type: taxRate. 
* 
* @param {object}  input                   Check schema for input.
* @param {string}  authMode                API_KEY (default), AWS_AIM or AMAZON_COGNITO_USER_POOLS
* 
* @return {string} Returns the newly updated taxRate.
*/
export async function updateTaxRate(input: UpdateTaxRateInput, authMode?: GRAPHQL_AUTH_MODE) {
   if (!input) return formatResult(false, "TaxRate", "No input", "Update TaxRate received no input.");
   try {
        const fullInput: UpdateTaxRateInput = {
            ...input,
            updatedOn: moment(new Date()).format("YYYY-MM-DDTHH:mm:ss.SSSZ")
        };
       let result;
       if (!authMode) result = (await API.graphql(graphqlOperation(mutations.updateTaxRate, { input: fullInput }))) as GraphQLResult<UpdateTaxRateMutation>;
       else result = (await API.graphql({
           query: mutations.updateTaxRate,
           variables: {
               input: fullInput
           },
           authMode: authMode || GRAPHQL_AUTH_MODE.API_KEY
       })) as GraphQLResult<UpdateTaxRateMutation>;
       const taxRate = result.data?.updateTaxRate;
       return formatResult(true, "TaxRate", taxRate, "Successfully updated the taxRate.");
   } catch (error: any) {
       return formatResult(false, "TaxRate", error, "Error updating record in the database for type: taxRate");
   }
}

/**
* Handle deleting a new record in the database for type: taxRate. 
* 
* @param {object}  input                   Just requires the ID of the object.
* @param {string}  authMode                API_KEY (default), AWS_AIM or AMAZON_COGNITO_USER_POOLS
* 
* @return {string} Returns the newly deleted taxRate.
*/
export async function deleteTaxRate(input: DeleteTaxRateInput, authMode?: GRAPHQL_AUTH_MODE) {
   if (!input) return formatResult(false, "TaxRate", "No input", "Delete TaxRate received no input.");
   try {
       let result;
       if (!authMode) result = (await API.graphql(graphqlOperation(mutations.deleteTaxRate, { input: input }))) as GraphQLResult<DeleteTaxRateMutation>;
       else result = (await API.graphql({
           query: mutations.deleteTaxRate,
           variables: {
               input: input
           },
           authMode: authMode || GRAPHQL_AUTH_MODE.API_KEY
       })) as GraphQLResult<DeleteTaxRateMutation>;
       const taxRate = result.data?.deleteTaxRate;
       return formatResult(true, "TaxRate", taxRate, "Successfully deleted the taxRate.");
   } catch (error: any) {
       return formatResult(false, "TaxRate", error, "Error deleting record in the database for type: taxRate");
   }
}

/**
* Get all records in the database for type: taxRate. 
* 
* @param {string}  authMode            API_KEY (default), AWS_AIM or AMAZON_COGNITO_USER_POOLS
* 
* @return {object} Returns the taxRate object
*/
export async function getAllTaxRates(authMode?: GRAPHQL_AUTH_MODE) {
    try {
        const result = (await API.graphql({
            query: queries.listTaxRates,
            variables: {
                limit: 1000
            },
            authMode: authMode || GRAPHQL_AUTH_MODE.API_KEY
        })) as GraphQLResult<ListTaxRatesQuery>;
        
        let items = result.data?.listTaxRates?.items as TaxRate[];
        let nextToken = result.data?.listTaxRates?.nextToken;

        while (nextToken) {
            const nextResult = await API.graphql({
                query: queries.listTaxRates,
                variables: {
                    limit: 100,
                    nextToken
                },
                authMode: authMode || GRAPHQL_AUTH_MODE.API_KEY
            }) as GraphQLResult<ListTaxRatesQuery>;

            const nextItems = nextResult.data?.listTaxRates?.items as TaxRate[];
            if (nextItems && nextItems.length) {
                items = items?.concat(nextItems);
            }

            nextToken = nextResult.data?.listTaxRates?.nextToken;
        }

        return formatResult(true, "TaxRate", items, "Successfully got the TaxRates.");
    } catch (error: any) {
        return formatResult(false, "TaxRate", error, "Error reading record in the database for type: taxRates");
    }
}

/**
* Read a specific record in the database for type: taxRate. 
* 
* @param {string}  id                  The taxRate id.
* @param {string}  authMode            API_KEY (default), AWS_AIM or AMAZON_COGNITO_USER_POOLS
* 
* @return {object} Returns the taxRate object
*/
export async function getTaxRateById(id: string, authMode?: GRAPHQL_AUTH_MODE) {
   try {
       const result = (await API.graphql({
           query: queries.getTaxRate,
           variables: {
               id: id
           },
           authMode: authMode || GRAPHQL_AUTH_MODE.API_KEY
       })) as GraphQLResult<GetTaxRateQuery>;
       const taxRate = result.data?.getTaxRate;
       return formatResult(true, "TaxRate", taxRate, "Successfully got the taxRate.");
   } catch (error: any) {
       return formatResult(false, "TaxRate", error, "Error reading record in the database for type: taxRate");
   }
}

/**
* Get record in the database that match the given criteria for type: taxRate. 
* 
* @param {string}  organizationId      The organization id
* @param {string}  authMode            API_KEY (default), AWS_AIM or AMAZON_COGNITO_USER_POOLS
* 
* @return {object} Returns the taxRates objects
*/
export async function getTaxRatesByOrganizationId(organizationId: string, authMode?: GRAPHQL_AUTH_MODE) {
    try {
        const filter = {organizationId: {eq: organizationId}};
        const result = (await API.graphql({
            query: queries.listTaxRates,
            variables: {
                limit: 100,
                filter
            },
            authMode: authMode || GRAPHQL_AUTH_MODE.API_KEY
        })) as GraphQLResult<ListTaxRatesQuery>;

        let items = result.data?.listTaxRates?.items;
        let nextToken = result.data?.listTaxRates?.nextToken;

        while (nextToken) {
            const nextResult = await API.graphql({
                query: queries.listTaxRates,
                variables: {
                    limit: 100,
                    filter,
                    nextToken
                },
                authMode: authMode || GRAPHQL_AUTH_MODE.API_KEY
            }) as GraphQLResult<ListTaxRatesQuery>;

            const nextItems = nextResult.data?.listTaxRates?.items;
            if (nextItems && nextItems.length) {
                items = items?.concat(nextItems);
            }

            nextToken = nextResult.data?.listTaxRates?.nextToken;
        }

        if (items) return formatResult(true, "TaxRate", items, "Successfully got the taxRates.");
        else return formatResult(false, "TaxRate", null, "Could not find the taxRates.");
    } catch (error: any) {
        return formatResult(false, "TaxRate", error, "Error reading record in the database for type: taxRates");
    }
}